<script lang="ts">
  import { lister, supprimer, type FicheEnBase } from '../donnees/personnages'
  import {
    adopterLaVersionDuServeur,
    imposerLaVersionLocale,
    synchroniser,
    synchronisationConfiguree,
    type Bilan,
  } from '../donnees/synchronisation'
  import { aller } from '../routage'
  import { referentiel } from '../rules'

  const classes = referentiel().classes

  let fiches = $state<FicheEnBase[]>([])
  let chargement = $state(true)
  /** Le personnage dont la suppression attend confirmation. */
  let aSupprimer = $state<string | null>(null)

  /* Le rendez-vous avec le serveur. Il n'est jamais sur le chemin critique :
     une partie se joue entièrement hors ligne, et l'absence de réseau n'est pas
     une panne mais le cas nominal (ADR-001). */
  const synchronisable = synchronisationConfiguree()
  let enCours = $state(false)
  let bilan = $state<Bilan | null>(null)

  async function prendreRendezVous() {
    enCours = true
    bilan = await synchroniser()
    enCours = false
    await rafraichir()
  }

  async function trancher(id: string, revisionServeur: number, pourLeServeur: boolean) {
    if (pourLeServeur) await adopterLaVersionDuServeur(id)
    else await imposerLaVersionLocale(id, revisionServeur)
    await prendreRendezVous()
  }

  async function rafraichir() {
    fiches = await lister()
    chargement = false
  }

  $effect(() => {
    void rafraichir()
  })

  async function confirmerSuppression(id: string) {
    await supprimer(id)
    aSupprimer = null
    await rafraichir()
  }

  /** La Classe courante, sous son nom imprimé — jamais son identifiant. */
  function nomDeClasse(f: FicheEnBase): string {
    const id = f.personnage.classes.at(-1)
    if (!id) return ''
    return classes.find((c) => c.id === id)?.nom ?? id
  }

  const leJour = (iso: string) =>
    new Date(iso).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
</script>

<header>
  <h1>Mes personnages</h1>
  <span class="gestes">
    {#if synchronisable}
      <button type="button" class="pilule" disabled={enCours} onclick={prendreRendezVous}>
        {enCours ? 'En cours…' : 'Synchroniser'}
      </button>
    {/if}
    <button type="button" class="principal" onclick={() => aller('/creation')}>
      Nouveau personnage
    </button>
  </span>
</header>

{#if bilan}
  <div class="bilan" class:panne={bilan.panne !== null}>
    {#if bilan.panne}
      <p>
        Rendez-vous manqué : {bilan.panne}. Vos fiches sont intactes — rien ici
        n'a besoin du réseau.
        {#if bilan.panne.includes('injoignable')}
          Si l'application vient d'être mise à jour, rechargez la page : le
          service worker peut encore servir la version précédente.
        {/if}
        {#if bilan.panne.includes('portail')}
          <a href="https://auth.dbernard2027.fr" target="_blank" rel="noreferrer">
            Ouvrir le portail
          </a>, puis recommencez.
        {/if}
      </p>
    {:else}
      <p>
        {bilan.envoyees} envoyée{bilan.envoyees > 1 ? 's' : ''},
        {bilan.recues} reçue{bilan.recues > 1 ? 's' : ''}.
        {#if bilan.conflits.length === 0}Tout concorde.{/if}
      </p>
    {/if}

    <!-- Un conflit ne se tranche pas tout seul : les deux versions restent
         entières, et c'est vous qui choisissez laquelle survit. -->
    {#each bilan.conflits as c (c.id)}
      <div class="conflit">
        <p>
          <strong>{c.nom}</strong> a divergé : un autre appareil a déposé depuis
          votre dernier rendez-vous. Sa révision est
          <span class="data">{c.revision_serveur}</span>, la vôtre
          <span class="data">{c.revision_locale}</span>.
        </p>
        <span class="gestes">
          <button type="button" class="pilule" onclick={() => trancher(c.id, c.revision_serveur, true)}>
            Prendre celle du serveur
          </button>
          <button type="button" class="pilule" onclick={() => trancher(c.id, c.revision_serveur, false)}>
            Imposer la mienne
          </button>
        </span>
      </div>
    {/each}
  </div>
{/if}

{#if chargement}
  <p class="note">Ouverture de la base…</p>
{:else if fiches.length === 0}
  <p class="vide">
    Aucun personnage pour l'instant. La Création se fait en sept étapes, et rien
    n'oblige à la finir d'une traite — le brouillon reste en place.
  </p>
{:else}
  <ul class="liste">
    {#each fiches as f (f.id)}
      <li>
        <button type="button" class="carte" onclick={() => aller(`/fiche/${f.id}`)}>
          <span class="nom">{f.nom}</span>
          <span class="detail">{nomDeClasse(f)}</span>
          <span class="date">modifié le {leJour(f.modifie_le)}</span>
        </button>
        {#if aSupprimer === f.id}
          <span class="confirmation">
            Supprimer définitivement&nbsp;?
            <button type="button" class="lien danger" onclick={() => confirmerSuppression(f.id)}>
              oui
            </button>
            <button type="button" class="lien" onclick={() => (aSupprimer = null)}>
              non
            </button>
          </span>
        {:else}
          <button
            type="button"
            class="lien"
            aria-label="Supprimer {f.nom}"
            onclick={() => (aSupprimer = f.id)}
          >
            supprimer
          </button>
        {/if}
      </li>
    {/each}
  </ul>
{/if}

<style>
  header .gestes {
    display: flex;
    align-items: center;
    gap: calc(var(--pas) * 2);
    flex-wrap: wrap;
  }
  /* Le compte rendu du rendez-vous : discret quand tout va bien, franc quand il
     y a quelque chose à trancher. */
  .bilan {
    border-left: 2px solid var(--ok);
    padding: var(--pas) calc(var(--pas) * 2);
    margin-bottom: calc(var(--pas) * 3);
    font-size: 0.85rem;
    color: var(--ink-soft);
  }
  .bilan.panne {
    border-left-color: var(--line-fort);
  }
  .bilan p {
    margin: 0 0 calc(var(--pas) / 2);
  }
  .conflit {
    border-top: 1px solid var(--line);
    padding-top: var(--pas);
    margin-top: var(--pas);
    color: var(--ink);
  }
  .conflit .gestes {
    display: flex;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: calc(var(--pas) * 3);
    flex-wrap: wrap;
    margin-bottom: calc(var(--pas) * 6);
  }
  h1 {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: clamp(1.1rem, 4vw, 1.6rem);
    margin: 0;
    color: var(--accent);
  }
  .principal {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.8rem;
    font-weight: 700;
    background: var(--accent);
    color: var(--accent-txt);
    border: 1px solid var(--line-fort);
    border-radius: var(--r);
    min-height: 44px;
    padding: 0 calc(var(--pas) * 4);
    cursor: pointer;
  }
  .principal:hover {
    filter: brightness(1.18);
  }
  .note,
  .vide {
    color: var(--ink-soft);
    max-width: 62ch;
  }
  .liste {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: calc(var(--pas) * 2);
  }
  .liste li {
    display: flex;
    align-items: center;
    gap: calc(var(--pas) * 3);
  }
  .carte {
    flex: 1;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: calc(var(--pas) * 0.5) calc(var(--pas) * 3);
    text-align: left;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--r);
    padding: calc(var(--pas) * 3);
    color: var(--ink);
    cursor: pointer;
    font: inherit;
  }
  .carte:hover {
    border-color: var(--line-fort);
  }
  .nom {
    font-family: 'Cinzel', serif;
    font-size: 1.05rem;
  }
  .detail {
    color: var(--gold);
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-align: right;
  }
  .date {
    font-size: 0.75rem;
    font-family: system-ui, sans-serif;
    color: var(--ink-soft);
    grid-column: 1 / -1;
  }
  .lien {
    background: none;
    border: none;
    color: var(--ink-soft);
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    font-size: 0.8rem;
  }
  .lien.danger {
    color: var(--ko);
  }
  .confirmation {
    font-size: 0.8rem;
    color: var(--ink-soft);
    display: flex;
    align-items: center;
    gap: var(--pas);
  }
</style>
